import type { ResolvedInstallationOrder } from "../types/installation";
import { isManualUnitPriceLine } from "utils/orderResolveLinePricing";
import { round2 } from "utils/fixedAmountDiscountAllocation";
import {
  readInstallationLineTaxable,
  readResolveRequired,
  resolveTaxableFromSources,
} from "../utils/readResolveTaxable";
import {
  inferInstallationDiscountScopeFromResolved,
  splitInstallationDiscountForPayload,
} from "./installationDiscountScope";

/**
 * Overlay POST /installation_orders financial fields from last resolve response.
 * Structural fields (addresses, installer, inventory rows) stay from the page builder.
 */
export function overlayInstallationSubmitFromResolved(
  payload: Record<string, unknown>,
  resolved: ResolvedInstallationOrder | null | undefined
): Record<string, unknown> {
  if (!resolved) return payload;

  const scope = inferInstallationDiscountScopeFromResolved(resolved);
  const { rootDiscountCode } = splitInstallationDiscountForPayload(
    String(resolved.discount_code ?? ""),
    scope
  );

  const out = { ...payload };
  const lineItems = Array.isArray(out.line_items) ? [...(out.line_items as any[])] : [];
  const hasManualLine = lineItems.some((li) => isManualUnitPriceLine(li));

  if (!hasManualLine) {
    if (resolved.subtotal_amount != null) out.subtotal_amount = resolved.subtotal_amount;
    if (resolved.total_amount != null) out.total_amount = resolved.total_amount;
  }
  if (resolved.installation_cost != null) out.installation_cost = resolved.installation_cost;
  if (resolved.additional_cost != null) out.additional_cost = resolved.additional_cost;
  if (resolved.discount_amount != null) {
    out.discount_amount = scope === "installation" ? resolved.discount_amount : 0;
  }
  if (resolved.discount_code != null) {
    out.discount_code = scope === "installation" ? rootDiscountCode : "";
  }
  (out as { discount_applies_to?: string }).discount_applies_to = scope;
  if (resolved.installation_tax_amount != null) {
    out.installation_tax_amount = resolved.installation_tax_amount;
  }
  if (resolved.installation_tax_percentage != null) {
    out.installation_tax_percentage = resolved.installation_tax_percentage;
  }
  if (resolved.over_all_tax_amount != null) out.over_all_tax_amount = resolved.over_all_tax_amount;
  if (resolved.over_all_tax_percentage != null) {
    out.over_all_tax_percentage = resolved.over_all_tax_percentage;
  }
  if (resolved.tax_status != null) out.tax_status = resolved.tax_status;
  if (resolved.cart_id) out.cart_id = resolved.cart_id;

  if (Array.isArray(resolved.specialfee) && resolved.specialfee.length > 0) {
    const originalById = new Map(
      (Array.isArray(out.specialfee) ? (out.specialfee as Record<string, unknown>[]) : []).map(
        (f) => [String(f.specialfee_id ?? f.id ?? ""), f]
      )
    );
    out.specialfee = resolved.specialfee.map((f) => {
      const id = String(f.specialfee_id || "");
      const original = originalById.get(id);
      const taxable = resolveTaxableFromSources(
        [original, f as Record<string, unknown>],
        ["specialfee_taxable", "taxable", "is_taxable"],
        true
      );
      return {
        specialfee_id: id,
        specialfee_name: String(f.specialfee_name || ""),
        specialfee_amount: Number(f.specialfee_amount || 0),
        specialfee_tax_percentage: Number(f.specialfee_tax_percentage || 0),
        specialfee_tax_amount: Number(f.specialfee_tax_amount || 0),
        specialfee_taxable: taxable,
        required: readResolveRequired(original) || f.required === true,
      };
    });
  }

  const resolvedByKey = new Map(
    (resolved.line_items || []).map((li) => [
      `${String(li.product_id || "")}:${String(li.variant_id || "")}`,
      li,
    ])
  );

  out.line_items = lineItems.map((li: any) => {
    if (li?.is_new_purchase !== true) return li;
    const key = `${String(li.product_id || "")}:${String(li.variant_id || "")}`;
    const r = resolvedByKey.get(key);
    if (!r) return li;

    if (isManualUnitPriceLine(li)) {
      const qty = Math.max(1, Number(li.quantity) || 1);
      const unit = Number(li.unit_price);
      const lineTax = Number(
        r.item_tax_info?.total_product_tax_amount ??
          li.item_tax_info?.total_product_tax_amount ??
          li.tax_amount ??
          0
      );
      const lineSub = round2(unit * qty);
      const lineTotal = round2(lineSub + lineTax);
      return {
        ...li,
        unit_price: unit,
        unit_retail_price: unit,
        changed_unit_price: unit,
        is_price_overridden: true,
        item_sub_total: lineSub,
        total_price: lineTotal,
        tax_amount: lineTax,
        discount: 0,
        discount_on_unit_quantity: 0,
        discount_on_total_quantity: 0,
        item_tax_info: r.item_tax_info ?? li.item_tax_info,
        discount_code: scope === "product" ? (r.discount_code ?? li.discount_code) : "",
      };
    }

    const rowDiscTotal = round2(
      Number(
        r.discount_on_total_quantity ??
          (r as { discount?: number }).discount ??
          li.discount_on_total_quantity ??
          li.discount ??
          0
      )
    );
    const qty = Math.max(1, Number(li.quantity) || 1);
    const perUnitDisc =
      r.discount_on_unit_quantity != null
        ? round2(Number(r.discount_on_unit_quantity))
        : rowDiscTotal > 0
          ? round2(rowDiscTotal / qty)
          : round2(Number(li.discount_on_unit_quantity ?? 0));

    return {
      ...li,
      unit_price: r.unit_price ?? li.unit_price,
      unit_retail_price: r.unit_price ?? li.unit_retail_price ?? li.unit_price,
      changed_unit_price: r.changed_unit_price ?? li.changed_unit_price,
      is_price_overridden:
        typeof r.is_price_overridden === "boolean"
          ? r.is_price_overridden
          : li.is_price_overridden,
      item_sub_total: r.item_sub_total ?? li.item_sub_total,
      total_price: r.total_price ?? li.total_price,
      item_tax_info: r.item_tax_info ?? li.item_tax_info,
      discount: rowDiscTotal,
      discount_on_unit_quantity: perUnitDisc,
      discount_on_total_quantity: rowDiscTotal,
      tax_amount: r.item_tax_info?.total_product_tax_amount ?? li.tax_amount,
      discount_code: scope === "product" ? (r.discount_code ?? li.discount_code) : "",
    };
  });

  return out;
}
